import type { RawParams } from "@uirouter/core";

import type { ICustomDimensionData, ITrack } from "@similarweb/sw-track";
import { AggregatedTracker } from "@similarweb/sw-track";

import { isSalesExtensionPresent } from "app/pages/sales-intelligence/utils/extension";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";
import { markPageViewTracked } from "app/services/track/pageViewTrackSignal";
import { getPageViewDropContextFromState, reportDroppedPageView } from "app/services/track/reportDroppedPageView";
import { getPageViewTrackingParams } from "app/services/track/utils/getPageViewTrackingParams";

export interface PageViewTrackers {
    matomoTracker: AggregatedTracker;
    mixpanelTracker: AggregatedTracker;
    munchkinTracker: AggregatedTracker;
    datadogTracker: AggregatedTracker;
}

export abstract class AllTrackersBaseService extends AggregatedTracker {
    private readonly pageViewTrackers: PageViewTrackers | null = null;

    constructor(trackers: ITrack[], pageViewTrackers: PageViewTrackers) {
        super(trackers);

        this.pageViewTrackers = pageViewTrackers;
    }

    protected trackPageViewWithTrackingParams(trackingParams: ICustomDimensionData): void {
        if (!this.pageViewTrackers) {
            return;
        }

        const { matomoTracker, mixpanelTracker, munchkinTracker, datadogTracker } = this.pageViewTrackers;

        matomoTracker.trackPageView(trackingParams);
        mixpanelTracker.trackPageView(trackingParams);
        munchkinTracker.trackPageView(trackingParams);
        datadogTracker.trackPageView(trackingParams);
    }

    abstract trackPageViewWithCustomUrl(toState: IRouterState, toParams: RawParams): Promise<void>;
}

export class AllTrackersService extends AllTrackersBaseService {
    public async trackPageViewWithCustomUrl(toState: IRouterState, toParams: RawParams) {
        // Must run synchronously before the first await: most handlers call SwTrack.trackPageView
        // without awaiting, so markPageViewTracked() must run in the same tick as the handler call.
        markPageViewTracked();

        const trackingParams = await getPageViewTrackingParams(toState, toParams);

        if (trackingParams == null) {
            reportDroppedPageView({
                reason: "tracking_params_null",
                ...getPageViewDropContextFromState(toState, toParams),
            });
            return;
        }

        const params = {
            ...trackingParams,
            custom_data: {
                ...(trackingParams.custom_data || {}),
                si_extension: isSalesExtensionPresent(),
            },
        };

        this.trackPageViewWithTrackingParams(params);
    }
}
